import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { useMolecule } from 'bunshi/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import { useMealPlanGeneration } from './useMealPlanGeneration';

/**
 * Auto-generates meal plan instructions when the meal plan card mounts
 * on the session prep tab and no instructions exist yet.
 *
 * Fires once per scope lifetime. If generation fails, an empty instructions
 * record is created via upsert so the provider can enter info manually.
 *
 * Must be rendered inside a MealPlanScope ScopeProvider.
 */
interface UseAutoGenerateInstructionsOptions {
  usePreviousAppointment?: boolean;
}

export function useAutoGenerateInstructions(
  options?: UseAutoGenerateInstructionsOptions,
) {
  const { usePreviousAppointment = true } = options ?? {};
  const {
    mealPlanInstructionsAtom,
    areMealPlanInstructionsLoadingAtom,
    previousAppointmentQueryAtom,
    refetchMealPlanInstructionsAtom,
  } = useMolecule(mealPlanMolecule);

  const instructions = useAtomValue(mealPlanInstructionsAtom);
  const isInstructionsLoading = useAtomValue(
    areMealPlanInstructionsLoadingAtom,
  );
  const previousAppointmentQuery = useAtomValue(previousAppointmentQueryAtom);
  const isPreviousAppointmentLoading =
    usePreviousAppointment && previousAppointmentQuery.isLoading;
  const refetchInstructions = useSetAtom(refetchMealPlanInstructionsAtom);

  const { generateInstructions, isGeneratingInstructions } =
    useMealPlanGeneration({ usePreviousAppointment, silentInstructions: true });

  const hasAttemptedRef = useRef(false);

  useEffect(
    function autoGenerateInstructionsOnMount() {
      if (hasAttemptedRef.current) return;
      if (isInstructionsLoading || isPreviousAppointmentLoading) return;

      // Instructions already exist — nothing to generate
      if (instructions) return;

      hasAttemptedRef.current = true;

      generateInstructions().then(async (result) => {
        if (result) return; // success — instructions were set by the mutation

        // Generation failed — refetch to pick up the empty record the backend
        // upserted, so the provider can edit it manually.
        datadogLogs.logger.info(
          'Auto-generation returned null, refetching for empty record',
          logContext('meal-plan', 'generate-instructions', {
            triggerType: 'auto',
          }),
        );
        await refetchInstructions();
      });
    },
    [
      instructions,
      isInstructionsLoading,
      isPreviousAppointmentLoading,
      generateInstructions,
      refetchInstructions,
    ],
  );

  // True once prerequisites are met AND any auto-generation has finished.
  const isReady =
    !isInstructionsLoading &&
    !isPreviousAppointmentLoading &&
    !isGeneratingInstructions;

  return { isGeneratingInstructions, isReady };
}
